import React, { useEffect, useState } from 'react';
import { HiRefresh, HiFilter } from 'react-icons/hi';
import apiClient from '../api/client';

export default function ChangeHistory({ clientId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    advert_id: '',
    parameter_type: '',
    change_source: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (clientId) {
      loadHistory();
    }
  }, [clientId, pagination.page, filters]);

  const loadHistory = async () => {
    if (!clientId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        ),
      });

      const response = await apiClient.get(`/change-history/${clientId}?${params}`);
      const data = response.data;
      
      console.log('Загружена история изменений:', {
        clientId,
        historyCount: data.history?.length || 0,
        total: data.pagination?.total || 0,
        data
      });
      
      setHistory(data.history || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
    } catch (error) {
      console.error('Ошибка загрузки истории изменений:', error);
      console.error('Детали ошибки:', error.response?.data || error.message);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      advert_id: '',
      parameter_type: '',
      change_source: '',
      date_from: '',
      date_to: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    const dd = pad(date.getDate());
    const mm = pad(date.getMonth() + 1);
    const yy = String(date.getFullYear()).slice(-2);
    const timeStr = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    return `${dd}.${mm}.${yy} ${timeStr}`;
  };

  const getParameterTypeLabel = (type) => {
    const labels = {
      bid: 'Ставка',
      minus_phrase: 'Минус-фраза',
      budget: 'Бюджет',
      strategy: 'Стратегия',
      daily_limit: 'Дневной лимит',
      campaign_status: 'Статус кампании',
    };
    return labels[type] || type;
  };

  const getSourceLabel = (source) => {
    const labels = {
      web: 'Веб-интерфейс',
      api: 'API',
      system: 'Система',
      bidder: 'Биддер',
      auto_minus: 'Авто-минусация',
      budget_distribution: 'Распределение бюджета',
    };
    return labels[source] || source;
  };

  const formatChangeDescription = (description, item) => {
    let lines = String(description || '').split('\n').map((line) => line.trim()).filter(Boolean);

    if (item?.change_source === 'bidder' && item?.parameter_type === 'bid') {
      lines = lines.filter((line) => {
        if (line.startsWith('Ставка изменена:')) return true;
        if (line.startsWith('Стратегия:')) return true;
        if (line.startsWith('Артикул:')) return true;
        if (line.startsWith('Размещение:')) return true;
        if (line.startsWith('Ключевое слово:')) {
          const keyword = line.slice('Ключевое слово:'.length).trim();
          return keyword && !/^nm:\d+/i.test(keyword);
        }
        return false;
      });
    }

    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-start">
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium text-gray-700"
          >
            <HiFilter className="w-4 h-4" />
            Фильтры
          </button>
          <button
            onClick={loadHistory}
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg disabled:opacity-50 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-md"
          >
            <HiRefresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>
      </div>

      {/* Фильтры */}
      {showFilters && (
        <div className="glass-card p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">ID кампании</label>
              <input
                type="number"
                value={filters.advert_id}
                onChange={(e) => handleFilterChange('advert_id', e.target.value)}
                placeholder="Все"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Тип параметра</label>
              <select
                value={filters.parameter_type}
                onChange={(e) => handleFilterChange('parameter_type', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">Все</option>
                <option value="bid">Ставка</option>
                <option value="minus_phrase">Минус-фраза</option>
                <option value="budget">Бюджет</option>
                <option value="strategy">Стратегия</option>
                <option value="daily_limit">Дневной лимит</option>
                <option value="campaign_status">Статус кампании</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Источник</label>
              <select
                value={filters.change_source}
                onChange={(e) => handleFilterChange('change_source', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">Все</option>
                <option value="web">Веб-интерфейс</option>
                <option value="api">API</option>
                <option value="system">Система</option>
                <option value="bidder">Биддер</option>
                <option value="auto_minus">Авто-минусация</option>
                <option value="budget_distribution">Распределение бюджета</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Дата от</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Дата до</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Сбросить фильтры
            </button>
          </div>
        </div>
      )}

      {/* Таблица истории */}
      <div className="glass-card overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 250px)' }}>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-500">Загрузка...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">Нет записей в истории изменений</p>
            <p className="text-sm">Проверьте фильтры или попробуйте обновить страницу</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto overflow-y-auto flex-1">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Дата и время</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Кто изменил</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Кампания</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Группа</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Объявление</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Параметр изменения</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Что изменилось</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="p-4 text-sm text-gray-900">
                        <div>
                          <div className="font-medium text-gray-900">{item.changed_by}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{getSourceLabel(item.change_source)}</div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-900">
                        {item.campaign_name ? (
                          <div>
                            <div className="font-medium text-gray-900">{item.campaign_name}</div>
                            {item.advert_id && (
                              <div className="text-xs text-gray-500 mt-0.5">N° {item.advert_id}</div>
                            )}
                          </div>
                        ) : (
                          item.advert_id ? `N° ${item.advert_id}` : '—'
                        )}
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {item.group_name || '—'}
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {item.ad_name || '—'}
                      </td>
                      <td className="p-4">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {getParameterTypeLabel(item.parameter_type)}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-900">
                        <div className="text-sm">
                          {formatChangeDescription(item.change_description, item)}
                        </div>
                        {item.old_value && item.new_value
                          && !(item.change_source === 'bidder' && item.parameter_type === 'bid')
                          && !String(item.change_description || '').includes('Ставка изменена:') && (
                          <div className="mt-1 text-xs">
                            <span className="text-gray-500 line-through">{item.old_value}</span>{' '}
                            <span className="text-green-600 font-medium">→ {item.new_value}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between sticky bottom-0 z-10">
                <div className="text-sm text-gray-600">
                  Показано {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} из {pagination.total}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Назад
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600">
                    Страница {pagination.page} из {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Вперед
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

